import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames/bind';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import { ImageOutlinedIcon, MovieCreationOutlinedIcon, TextFieldsIcon } from 'tuui';
import { Divider, INDICATION_TYPES, RadioGroup, RadioIcon } from 'taboola-ultimate-ui';
import { useAvailableItems } from 'hooks';
import { CREATIVE_TYPE, isCreateOrDuplicateMode } from 'modules/campaigns/config';
import { FEATURE_FLAGS, useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import useFormFieldValue from 'modules/taboola-common-frontend-modules/formData/hooks/useFormFieldValue';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue, validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { aiTabReferenceImageSelector } from '../../../selectors';
import { useGenerativeAiDisclaimerModal } from './GenerativeAiDisclaimerModal/useGenerativeAiDisclaimerModal';
import { useHasAcceptedGenerativeAiDisclaimer } from './GenerativeAiDisclaimerModal/useHasAcceptedGenerativeAiDisclaimer';
import FromImageSubTabWithDragAndDrop from './subTabs/FromImageSubTabWithDragAndDrop';
import FromPromptSubTab from './subTabs/FromPromptSubTab';
import { ImageToMotionSubTab } from './subTabs/ImageToMotionSubTab/ImageToMotionSubTab';
import styles from './aiGeneratedImagesTab.module.scss';

const classNameBuilder = classnames.bind(styles);

const IMAGE_TO_MOTION = 'imageToMotion';
const FROM_PROMPT = 'fromPrompt';
const FROM_IMAGE = 'fromImage';

const subTabs = [
    {
        id: IMAGE_TO_MOTION,
        caption: {
            id: 'creative.editor.tab.aiGenerated.imageToMotion.title',
            defaultMessage: 'Image to motion',
        },
        Component: ImageToMotionSubTab,
        metricsAttributes: {
            'data-metrics-event-name': GTM_EVENTS.USABILITY,
            'data-metrics-component': 'IMAGE_TO_MOTION',
        },
        icon: <MovieCreationOutlinedIcon />,
        visibilityCondition: ({ creativeType, accountConfig, mode }) =>
            accountConfig[FEATURE_FLAGS.CREATE_IMAGE_TO_MOTION_ENABLED] === 'true' &&
            (isCreateOrDuplicateMode(mode) || creativeType === CREATIVE_TYPE.MOTION),
    },
    {
        id: FROM_PROMPT,
        caption: {
            id: 'creative.editor.tab.aiGenerated.fromPrompt.title',
            defaultMessage: 'Text to image',
        },
        Component: FromPromptSubTab,
        metricsAttributes: {
            'data-metrics-event-name': GTM_EVENTS.USABILITY,
            'data-metrics-component': 'FROM_PROMPT',
        },
        icon: <TextFieldsIcon />,
        visibilityCondition: ({ creativeType, mode }) =>
            isCreateOrDuplicateMode(mode) || creativeType === CREATIVE_TYPE.IMAGE,
    },
    {
        id: FROM_IMAGE,
        caption: {
            id: 'creative.editor.tab.aiGenerated.fromImage.title',
            defaultMessage: 'Image to image',
        },
        Component: FromImageSubTabWithDragAndDrop,
        metricsAttributes: {
            'data-metrics-event-name': GTM_EVENTS.USABILITY,
            'data-metrics-component': 'FROM_IMAGE',
        },
        icon: <ImageOutlinedIcon />,
        visibilityCondition: ({ creativeType, mode }) =>
            isCreateOrDuplicateMode(mode) || creativeType === CREATIVE_TYPE.IMAGE,
    },
];

const BRANDS = [
    'ford',
    'toyota',
    'honda',
    'chevrolet',
    'bmw',
    'mercedes-benz',
    'volkswagen',
    'audi',
    'porsche',
    'nissan',
    'hyundai',
    'kia',
    'peugeot',
    'fiat',
    'subaru',
    'mazda',
    'renault',
    'mitsubishi',
    'lexus',
    'land rover',
    'cadillac',
    'dodge',
    'jeep',
    'tesla',
    'volvo',
    'infiniti',
    'acura',
    'gmc',
    'chrysler',
    'lincoln',
    'buick',
    'ram',
    'alfa romeo',
    'bentley',
    'maserati',
    'rolls-royce',
    'aston martin',
    'lamborghini',
    'ferrari',
    'mclaren',
    'bugatti',
    'genesis',
    'samsung',
    'google',
    'microsoft',
    'sony',
    'intel',
    'dell',
    'hp',
    'lenovo',
    'lg',
];
const BRANDS_SET = new Set(BRANDS);

const promptValidations = [
    {
        validationFn: validationFunctions.isLength,
        options: { max: 400 },
        messageId: 'creative.editor.tab.aiGenerated.fromPrompt.validations.length',
        defaultMessage: 'We suggest keeping the prompt length under 400 characters',
        severity: INDICATION_TYPES.WARNING,
    },
    {
        validationFn: (value, _, { selectedTab }) => {
            if (selectedTab !== FROM_PROMPT) {
                return true;
            }

            const lowerCaseValueWords = (value || '').toLowerCase().split(/\s/);
            // return true if no word in the prompt is a brand
            return !lowerCaseValueWords.some(word => BRANDS_SET.has(word));
        },
        messageId: 'creative.editor.tab.aiGenerated.fromPrompt.brand.error',
        defaultMessage: 'Remove mentions of brands from your description to generate images',
    },
];

const AIGeneratedImagesTab = ({ onSelectMedia }) => {
    const { getHasAcceptedGenerativeAiDisclaimer } = useHasAcceptedGenerativeAiDisclaimer();
    const { openDisclaimerModal } = useGenerativeAiDisclaimerModal(onSelectMedia);
    const { accountId: selectedAccountId } = useAccount();
    const { value: creativeType } = useFormFieldValue({ field: 'creativeType' });
    const { mode } = useFormDataContext();
    const availableSubTabs = useAvailableItems(subTabs, { creativeType, mode });
    const initialSelectedTab = availableSubTabs.some(tab => tab.id === IMAGE_TO_MOTION) ? IMAGE_TO_MOTION : FROM_PROMPT;
    const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
    const {
        value: prompt,
        onChange: setPrompt,
        indicationData: promptFailedValidationData,
    } = useFormValidatedValue({
        field: 'aiImagesPrompt',
        validations: promptValidations,
        validationDependencies: { selectedTab },
    });
    const initialReferenceImage = useSelector(aiTabReferenceImageSelector);
    const [referenceImage, setReferenceImage] = useState(initialReferenceImage);
    const resolvedOnSelectMedia = useCallback(
        (...args) => (getHasAcceptedGenerativeAiDisclaimer() ? onSelectMedia(...args) : openDisclaimerModal(...args)),
        [getHasAcceptedGenerativeAiDisclaimer, onSelectMedia, openDisclaimerModal]
    );

    const tabHeaders = availableSubTabs.map(({ caption, icon, id, metricsAttributes }) => (
        <RadioIcon
            key={id}
            title={<FormattedMessage id={caption.id} defaultMessage={caption.defaultMessage} />}
            iconComponent={icon}
            value={id}
            hideRadioPointer={true}
            titleClassName={styles['title']}
            iconContainerClassName={classNameBuilder('icon-container', { selected: id === selectedTab })}
            labelClassName={styles['label']}
            checkedRadioColor={styles['checkedRadioColor']}
            {...metricsAttributes}
        />
    ));

    const tabContent = availableSubTabs.map(
        ({ Component, id }) =>
            Component &&
            id === selectedTab && (
                <Component
                    key={id}
                    onSelectMedia={resolvedOnSelectMedia}
                    selectedAccountId={selectedAccountId}
                    prompt={prompt}
                    setPrompt={setPrompt}
                    referenceImage={referenceImage}
                    setReferenceImage={setReferenceImage}
                    promptFailedValidationData={promptFailedValidationData}
                />
            )
    );

    return (
        <div className={styles['container']}>
            <Divider className={styles['divider']} />
            <div className={styles['header']}>
                <FormattedMessage
                    id="creative.editor.tab.aiGenerated.title"
                    defaultMessage="What would you like to generate?"
                />
            </div>
            <RadioGroup className={styles['group']} selectedValue={selectedTab} onChange={setSelectedTab}>
                {tabHeaders}
            </RadioGroup>
            {tabContent}
        </div>
    );
};

AIGeneratedImagesTab.propTypes = {
    onSelectMedia: PropTypes.func,
};

AIGeneratedImagesTab.defaultProps = {
    onSelectMedia: noop,
};

export default AIGeneratedImagesTab;
